import {
  Bot,
  Calendar,
  CheckCircle,
  CircleDollarSign,
  Eye,
  Globe,
  Linkedin,
  ListChecks,
  ListPlus,
  Mail,
  MessageSquare,
  Mic,
  MousePointer,
  Reply,
  MessageCircle,
  Phone,
  PhoneCall,
  PhoneMissed,
  User,
  Users,
  Clock,
  TrendingUp,
  Voicemail,
  FlaskConical,
} from "lucide-react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Plus } from "lucide-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import React, { useEffect } from "react";
import ActivityTimeline from "../../../components/ActivityTimeline";
import {
  DEMO_MODE_KEY,
  SEQUENCE_ACTION_TYPE_EMAIL,
  SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST,
  SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE,
  SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT,
  SEQUENCE_ACTION_TYPE_SMS,
  SEQUENCE_ACTION_TYPE_VOICE_MESSAGE,
  SEQUENCE_ACTION_TYPE_BROWSER_TASK,
  SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION,
  SEQUENCE_ACTION_TYPE_AUTO_AGENT,
  SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE,
  SEQUENCE_ACTION_TYPE_ADD_TO_LIST,
} from "../../../constants";
import { DEMO_SEQUENCE_STEP_METRICS, DEMO_SEQUENCES } from "../../../constants/demoData";
import { hostname, parseError } from "../../../utils";
import MetricCard from "../SequenceMetricsCard";
import SequenceMetricsCards from "../../../components/SequenceMetricsCards";
import SequenceActionsDialog from "../dialogs/SequenceActionsDialog";
import AddManualMetricDialog from "../dialogs/AddManualMetricDialog";
import ABTestMetricsCard from "./ABTestMetricsCard";

interface SequenceMetricsProps {
  sequenceId: string;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: Function;
  showTitle?: boolean;
}

export default ({
  sequenceId,
  companyId,
  getToken,
  eventListener,
  showTitle = true,
}: SequenceMetricsProps) => {
  const [loading, setLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState<any>(null);
  const [sequenceDetails, setSequenceDetails] = React.useState<any>(null);
  const [approvalsOpen, setApprovalsOpen] = React.useState(false);
  const [addMetricOpen, setAddMetricOpen] = React.useState(false);
  const [isDemoMode, setIsDemoMode] = React.useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  // Listen for demo mode changes
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };

    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };

    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const formatPercentage = (num: number) => {
    return Number(num).toFixed(2);
  };

  const getStepTypeMetrics = (step: any) => {
    const actionType =
      step.actionType || step.step_action_type || SEQUENCE_ACTION_TYPE_EMAIL;

    // Use the correct field names from the API response
    const totalExecutions = step.totalExecutions || step.total_executions || step.executedCount || 0;
    const failedCount = step.failedCount || step.failed_count || 0;
    const successRate = step.successRate || ((totalExecutions - failedCount) / totalExecutions * 100) || 0;

    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_EMAIL:
        return {
          primaryMetric: {
            icon: <Eye size={24} />,
            title: "Opens",
            value: step.unique_opens || 0,
            percentage: step.open_rate || 0,
            tooltip: "Unique recipients who opened this email",
          },
          secondaryMetric: {
            icon: <MousePointer size={24} />,
            title: "Clicks",
            value: step.unique_clicks || 0,
            percentage: step.click_rate || 0,
            tooltip: "Unique recipients who clicked a link in this email",
          },
          tertiaryMetric: {
            icon: <Reply size={24} />,
            title: "Replies",
            value: step.unique_replies || 0,
            percentage: step.reply_rate || 0,
            tooltip: "Positive replies only - excludes auto-replies and negative responses",
          },
          quaternaryMetric: (step.unique_bookings || 0) > 0 ? {
            icon: <Calendar size={24} />,
            title: "Bookings",
            value: step.unique_bookings || 0,
            percentage: step.booking_rate || 0,
            tooltip: "Meetings booked through scheduling links in this email",
          } : null,
        };
      case SEQUENCE_ACTION_TYPE_SMS:
        // Use new smsMetrics if available
        // totalExecutions includes both success and failed
        const smsMetrics = step.smsMetrics || {};
        const smsDelivered = smsMetrics.smsDelivered || 0;
        const smsDeliveryRate = smsMetrics.deliveryRate || successRate;
        const smsSuccessCount = totalExecutions - failedCount;
        const smsFailRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

        return {
          primaryMetric: {
            icon: <MessageSquare size={24} />,
            title: "Total",
            value: totalExecutions,
            percentage: null,
            tooltip: "Total SMS messages attempted",
          },
          secondaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Sent",
            value: smsSuccessCount,
            percentage: successRate,
            tooltip: `${successRate.toFixed(1)}% of SMS messages sent successfully`,
          },
          tertiaryMetric: (step.unique_replies || 0) > 0 ? {
            icon: <Reply size={24} />,
            title: "Replies",
            value: step.unique_replies || 0,
            percentage: step.reply_rate || 0,
            tooltip: "Positive replies only - excludes auto-replies and negative responses",
          } : (failedCount > 0 ? {
            icon: <PhoneMissed size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: smsFailRate,
            tooltip: `${smsFailRate.toFixed(1)}% of SMS messages failed to send`,
          } : null),
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        // totalExecutions = executed count (includes both success and failed)
        // failedCount = number that failed
        // successCount = totalExecutions - failedCount
        const vmSuccessCount = totalExecutions - failedCount;
        const vmFailRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

        return {
          primaryMetric: {
            icon: <Voicemail size={24} />,
            title: "Total",
            value: totalExecutions,
            percentage: null,
            tooltip: "Total voice message calls attempted",
          },
          secondaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Delivered",
            value: vmSuccessCount,
            percentage: successRate,
            tooltip: `${successRate.toFixed(1)}% of voice messages successfully delivered`,
          },
          tertiaryMetric: failedCount > 0 ? {
            icon: <PhoneMissed size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: vmFailRate,
            tooltip: `${vmFailRate.toFixed(1)}% of voice messages failed to deliver`,
          } : null,
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
        return {
          primaryMetric: failedCount > 0 ? {
            icon: <CheckCircle size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: (failedCount / (totalExecutions + failedCount) * 100),
            tooltip: "Profile visits that failed due to errors or rate limits",
          } : null,
          secondaryMetric: null,
          tertiaryMetric: null,
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
        return {
          primaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Success Rate",
            value: `${successRate.toFixed(2)}%`,
            percentage: successRate,
            tooltip: "Percentage of connection requests successfully sent",
          },
          secondaryMetric: failedCount > 0 ? {
            icon: <CheckCircle size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: (failedCount / (totalExecutions + failedCount) * 100),
            tooltip: "Connection requests that failed due to errors or rate limits",
          } : null,
          tertiaryMetric: null,
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return {
          primaryMetric: {
            icon: <MessageSquare size={24} />,
            title: "Sent",
            value: totalExecutions,
            percentage: null,
            tooltip: "Total LinkedIn messages sent",
          },
          secondaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Success Rate",
            value: `${successRate.toFixed(2)}%`,
            percentage: successRate,
            tooltip: "Percentage of messages successfully delivered",
          },
          tertiaryMetric: {
            icon: <Reply size={24} />,
            title: "Replies",
            value: step.unique_replies || 0,
            percentage: step.reply_rate || 0,
            tooltip: "Positive replies only - excludes auto-replies and negative responses",
          },
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
        return {
          primaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Success Rate",
            value: `${successRate.toFixed(2)}%`,
            percentage: successRate,
            tooltip: "Percentage of browser tasks completed successfully",
          },
          secondaryMetric: failedCount > 0 ? {
            icon: <CheckCircle size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: (failedCount / (totalExecutions + failedCount) * 100),
            tooltip: "Browser tasks that failed due to errors",
          } : null,
          tertiaryMetric: null,
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
        // Use new voiceMetrics and callIntelligence data if available
        const voiceMetrics = step.voiceMetrics || {};
        const callIntelligence = step.callIntelligence || {};
        const callsCompleted = voiceMetrics.callsCompleted || 0;
        const callsAnsweredHuman = voiceMetrics.callsAnsweredHuman || 0;
        const humanAnswerRate = voiceMetrics.humanAnswerRate || voiceMetrics.answerRate || 0;
        const engagementRate = callIntelligence.engagementRate || 0;
        const prospectsEngaged = callIntelligence.prospectsEngaged || 0;
        const meetingsBooked = callIntelligence.meetingsBooked || 0;
        const meetingBookedRate = callIntelligence.meetingBookedRate || 0;

        // If we have detailed voice metrics, use them
        if (voiceMetrics.callsCompleted !== undefined) {
          // totalExecutions = all executed (includes both success and failed since failed=true still has executed=true)
          // failedCount = number that failed to connect
          // callsConnected = totalExecutions - failedCount (successful connections)
          const callsConnected = voiceMetrics.callsCompleted || (totalExecutions - failedCount);
          const connectionRate = totalExecutions > 0 ? (callsConnected / totalExecutions) * 100 : 0;
          const failRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

          return {
            primaryMetric: {
              icon: <Phone size={24} />,
              title: "Total Calls",
              value: totalExecutions,
              percentage: null,
              tooltip: "Total voice calls attempted",
            },
            secondaryMetric: {
              icon: <PhoneCall size={24} />,
              title: "Connected",
              value: callsConnected,
              percentage: connectionRate,
              tooltip: `${connectionRate.toFixed(1)}% of calls connected successfully`,
            },
            tertiaryMetric: callsAnsweredHuman > 0 ? {
              icon: <User size={24} />,
              title: "Live Conversations",
              value: callsAnsweredHuman,
              percentage: humanAnswerRate,
              tooltip: `${humanAnswerRate.toFixed(1)}% of connected calls were live conversations (vs voicemail)`,
            } : (failedCount > 0 ? {
              icon: <PhoneMissed size={24} />,
              title: "Failed",
              value: failedCount,
              percentage: failRate,
              tooltip: `${failRate.toFixed(1)}% of calls failed to connect`,
            } : null),
            quaternaryMetric: (meetingsBooked > 0 || prospectsEngaged > 0) ? {
              icon: <TrendingUp size={24} />,
              title: meetingsBooked > 0 ? "Meetings" : "Engaged",
              value: meetingsBooked > 0 ? meetingsBooked : prospectsEngaged,
              percentage: meetingsBooked > 0 ? meetingBookedRate : engagementRate,
              tooltip: meetingsBooked > 0
                ? `${meetingsBooked} meetings booked from voice calls`
                : `${prospectsEngaged} prospects engaged in meaningful conversation`,
            } : null,
          };
        }

        // Fallback to basic metrics if voiceMetrics not available
        const acSuccessCount = totalExecutions - failedCount;
        const acFailRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

        return {
          primaryMetric: {
            icon: <Phone size={24} />,
            title: "Total Calls",
            value: totalExecutions,
            percentage: null,
            tooltip: "Total agent conversation calls attempted",
          },
          secondaryMetric: {
            icon: <CheckCircle size={24} />,
            title: "Connected",
            value: acSuccessCount,
            percentage: successRate,
            tooltip: `${successRate.toFixed(1)}% of calls connected successfully`,
          },
          tertiaryMetric: failedCount > 0 ? {
            icon: <PhoneMissed size={24} />,
            title: "Failed",
            value: failedCount,
            percentage: acFailRate,
            tooltip: `${acFailRate.toFixed(1)}% of calls failed to connect`,
          } : null,
          quaternaryMetric: null,
        };
      case SEQUENCE_ACTION_TYPE_AUTO_AGENT:
        // Auto agent metrics depend on the channel used
        // The backend will provide channel-specific metrics in autoAgentMetrics
        const autoAgentMetrics = step.autoAgentMetrics || {};
        const dominantChannel = autoAgentMetrics.dominantChannel || 'email';

        // Show metrics based on the dominant channel
        if (dominantChannel === 'email') {
          return {
            primaryMetric: {
              icon: <Eye size={24} />,
              title: "Opens",
              value: step.unique_opens || 0,
              percentage: step.open_rate || 0,
              tooltip: "Unique recipients who opened emails from this auto agent",
            },
            secondaryMetric: {
              icon: <MousePointer size={24} />,
              title: "Clicks",
              value: step.unique_clicks || 0,
              percentage: step.click_rate || 0,
              tooltip: "Unique recipients who clicked links in auto agent emails",
            },
            tertiaryMetric: {
              icon: <Reply size={24} />,
              title: "Replies",
              value: step.unique_replies || 0,
              percentage: step.reply_rate || 0,
              tooltip: "Positive replies to auto agent emails",
            },
            quaternaryMetric: (step.unique_bookings || 0) > 0 ? {
              icon: <Calendar size={24} />,
              title: "Bookings",
              value: step.unique_bookings || 0,
              percentage: step.booking_rate || 0,
              tooltip: "Meetings booked through auto agent emails",
            } : null,
          };
        } else if (dominantChannel === 'sms') {
          const smsSuccessCount = totalExecutions - failedCount;
          const smsFailRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

          return {
            primaryMetric: {
              icon: <MessageSquare size={24} />,
              title: "Total",
              value: totalExecutions,
              percentage: null,
              tooltip: "Total SMS messages sent by auto agent",
            },
            secondaryMetric: {
              icon: <CheckCircle size={24} />,
              title: "Sent",
              value: smsSuccessCount,
              percentage: successRate,
              tooltip: `${successRate.toFixed(1)}% of auto agent SMS messages sent successfully`,
            },
            tertiaryMetric: (step.unique_replies || 0) > 0 ? {
              icon: <Reply size={24} />,
              title: "Replies",
              value: step.unique_replies || 0,
              percentage: step.reply_rate || 0,
              tooltip: "Positive replies to auto agent SMS",
            } : (failedCount > 0 ? {
              icon: <PhoneMissed size={24} />,
              title: "Failed",
              value: failedCount,
              percentage: smsFailRate,
              tooltip: `${smsFailRate.toFixed(1)}% of auto agent SMS messages failed`,
            } : null),
            quaternaryMetric: null,
          };
        } else if (dominantChannel === 'voice') {
          const voiceSuccessCount = totalExecutions - failedCount;
          const voiceFailRate = totalExecutions > 0 ? (failedCount / totalExecutions) * 100 : 0;

          return {
            primaryMetric: {
              icon: <Voicemail size={24} />,
              title: "Total",
              value: totalExecutions,
              percentage: null,
              tooltip: "Total voice messages sent by auto agent",
            },
            secondaryMetric: {
              icon: <CheckCircle size={24} />,
              title: "Delivered",
              value: voiceSuccessCount,
              percentage: successRate,
              tooltip: `${successRate.toFixed(1)}% of auto agent voice messages delivered`,
            },
            tertiaryMetric: failedCount > 0 ? {
              icon: <PhoneMissed size={24} />,
              title: "Failed",
              value: failedCount,
              percentage: voiceFailRate,
              tooltip: `${voiceFailRate.toFixed(1)}% of auto agent voice messages failed`,
            } : null,
            quaternaryMetric: null,
          };
        }

        // Fallback if channel is unknown
        return {
          primaryMetric: null,
          secondaryMetric: null,
          tertiaryMetric: null,
          quaternaryMetric: null,
        };
      default:
        return {
          primaryMetric: null,
          secondaryMetric: null,
          tertiaryMetric: null,
          quaternaryMetric: null,
        };
    }
  };

  const getStepIcon = (step: any) => {
    const actionType =
      step.actionType || step.step_action_type || SEQUENCE_ACTION_TYPE_EMAIL;

    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_EMAIL:
        return <Mail size={24} />;
      case SEQUENCE_ACTION_TYPE_SMS:
        return <MessageCircle size={24} />;
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        return <Mic size={24} />;
      case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
        return <Bot size={24} />;
      case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return <Linkedin size={24} />;
      case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
        return <Globe size={24} />;
      case SEQUENCE_ACTION_TYPE_AUTO_AGENT:
        return <Bot size={24} />;
      case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
        return <ListChecks size={24} />;
      case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
        return <ListPlus size={24} />;
      default:
        return <Mail size={24} />;
    }
  };

  const getStepTypeLabel = (step: any) => {
    const actionType =
      step.actionType || step.step_action_type || SEQUENCE_ACTION_TYPE_EMAIL;

    switch (actionType) {
      case SEQUENCE_ACTION_TYPE_EMAIL:
        return "Email";
      case SEQUENCE_ACTION_TYPE_SMS:
        return "SMS";
      case SEQUENCE_ACTION_TYPE_VOICE_MESSAGE:
        return "Voice Message";
      case SEQUENCE_ACTION_TYPE_AGENT_CONVERSATION:
        return "Agent Conversation";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_PROFILE_VISIT:
        return "Profile Visit";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_CONNECTION_REQUEST:
        return "Connection Request";
      case SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE:
        return "LinkedIn Message";
      case SEQUENCE_ACTION_TYPE_BROWSER_TASK:
        return "Browser Task";
      case SEQUENCE_ACTION_TYPE_AUTO_AGENT:
        return "Auto Agent Loop";
      case SEQUENCE_ACTION_TYPE_ADD_TO_SEQUENCE:
        return "Add to Sequence";
      case SEQUENCE_ACTION_TYPE_ADD_TO_LIST:
        return "Add to List";
      default:
        return "Unknown";
    }
  };

  const getChartData = () => {
    const steps = metrics?.stepMetrics || metrics?.steps || [];
    if (!metrics || !steps || steps.length === 0) return null;

    const chartData = {
      xAxis: [
        {
          scaleType: "band" as const,
          data: steps.map(
            (step: any) => `Step ${step.stepOrder || step.step_order || steps.indexOf(step) + 1}`
          ),
        },
      ],
      series: [
        {
          label: "Sent",
          data: steps.map((step: any) => step.totalExecutions || step.total_executions || step.executedCount || 0),
          color: "#1976d2",
        },
      ] as any[],
    };

    // Add series based on step types - prioritize email metrics first, then LinkedIn-specific ones
    const hasEmailSteps = steps.some(
      (step: any) =>
        (step.actionType ||
          step.step_action_type ||
          SEQUENCE_ACTION_TYPE_EMAIL) === SEQUENCE_ACTION_TYPE_EMAIL
    );

    const hasLinkedinSteps = steps.some((step: any) => {
      const actionType =
        step.actionType || step.step_action_type || SEQUENCE_ACTION_TYPE_EMAIL;
      return actionType.includes("linkedin");
    });

    if (hasEmailSteps) {
      // Add email-specific series
      chartData.series.push({
        label: "Opens",
        data: steps.map((step: any) => {
          const actionType =
            step.actionType ||
            step.step_action_type ||
            SEQUENCE_ACTION_TYPE_EMAIL;
          return actionType === SEQUENCE_ACTION_TYPE_EMAIL
            ? step.unique_opens || 0
            : 0;
        }),
        color: "#0288d1",
      });

      chartData.series.push({
        label: "Clicks",
        data: steps.map((step: any) => {
          const actionType =
            step.actionType ||
            step.step_action_type ||
            SEQUENCE_ACTION_TYPE_EMAIL;
          return actionType === SEQUENCE_ACTION_TYPE_EMAIL
            ? step.unique_clicks || 0
            : 0;
        }),
        color: "#00c853",
      });
    }

    // Add replies for both email and LinkedIn message steps
    if (hasEmailSteps || hasLinkedinSteps) {
      chartData.series.push({
        label: "Replies",
        data: steps.map((step: any) => {
          const actionType =
            step.actionType ||
            step.step_action_type ||
            SEQUENCE_ACTION_TYPE_EMAIL;
          return actionType === SEQUENCE_ACTION_TYPE_EMAIL ||
            actionType === SEQUENCE_ACTION_TYPE_LINKEDIN_MESSAGE
            ? step.unique_replies || 0
            : 0;
        }),
        color: "#ff9800",
      });
    }

    return chartData;
  };

  const loadMetrics = async () => {
    if (!sequenceId) return;

    if (isDemoMode) {
      setLoading(true);
      setTimeout(() => {
        // Find the demo sequence by ID to get its name
        const demoSequence = DEMO_SEQUENCES.find((s) => s.id === sequenceId);
        const demoMetrics = {
          ...DEMO_SEQUENCE_STEP_METRICS,
          sequence_name: demoSequence?.name || DEMO_SEQUENCE_STEP_METRICS.sequence_name,
        };
        setMetrics(demoMetrics);
        setSequenceDetails({
          name: demoSequence?.name || DEMO_SEQUENCE_STEP_METRICS.sequence_name,
          totalMembers: demoSequence?.memberCount || DEMO_SEQUENCE_STEP_METRICS.total_members,
          prospectValue: demoSequence?.prospectValue || 15000,
        });
        setLoading(false);
      }, 300);
      return;
    }

    setLoading(true);
    const currentToken = await getToken();

    try {
      // Fetch step metrics
      const metricsRes = await axios.get(
        `${hostname}/sequences/${sequenceId}/step-metrics`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      // Validate that we got valid data
      const metricsData = metricsRes.data;
      if (metricsData && typeof metricsData === "object") {
        setMetrics(metricsData);
      } else {
        console.warn("Invalid metrics data received:", metricsData);
        setMetrics(null);
      }

      // Fetch sequence details using the dedicated endpoint
      try {
        const sequenceRes = await axios.get(
          `${hostname}/sequences/${sequenceId}`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${currentToken}` },
          }
        );
        setSequenceDetails(sequenceRes.data);
      } catch (sequenceErr) {
        console.warn("Failed to fetch sequence details:", sequenceErr);
        setSequenceDetails(null);
      }
    } catch (err) {
      console.error("Error loading metrics:", err);
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      // Reset state on error
      setMetrics(null);
      setSequenceDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sequenceId) {
      loadMetrics();
    }
  }, [sequenceId, isDemoMode]);

  if (!loading && !metrics) {
    return (
      <Box p={4}>
        <Typography variant="body1" color="text.secondary">
          No metrics available for this sequence.
        </Typography>
      </Box>
    );
  }

  // Initialize default values if metrics properties are missing
  const steps = metrics?.stepMetrics || metrics?.steps || [];

  // Calculate totals from stepMetrics if not provided
  const calculateTotalsFromSteps = (stepData: any[]) => {
    const defaultTotals = {
      total_executions: 0,
      total_unique_opens: 0,
      total_unique_clicks: 0,
      total_unique_replies: 0,
      total_bounces: 0,
      overall_open_rate: 0,
      overall_click_rate: 0,
      overall_reply_rate: 0,
      overall_bounce_rate: 0,
    };

    if (!stepData || stepData.length === 0) return defaultTotals;

    return {
      total_executions: stepData.reduce((sum, step) =>
        sum + (step.totalExecutions || step.total_executions || step.executedCount || 0), 0),
      total_unique_opens: stepData.reduce((sum, step) =>
        sum + (step.unique_opens || 0), 0),
      total_unique_clicks: stepData.reduce((sum, step) =>
        sum + (step.unique_clicks || 0), 0),
      total_unique_replies: stepData.reduce((sum, step) =>
        sum + (step.unique_replies || 0), 0),
      total_bounces: 0, // Bounces come from the backend totals, not step-level
      overall_open_rate: 0, // Will calculate below
      overall_click_rate: 0,
      overall_reply_rate: 0,
      overall_bounce_rate: 0,
    };
  };

  const calculatedTotals = calculateTotalsFromSteps(steps);

  // Calculate rates based on totals
  if (calculatedTotals.total_executions > 0) {
    calculatedTotals.overall_open_rate = (calculatedTotals.total_unique_opens / calculatedTotals.total_executions * 100);
    calculatedTotals.overall_click_rate = (calculatedTotals.total_unique_clicks / calculatedTotals.total_executions * 100);
    calculatedTotals.overall_reply_rate = (calculatedTotals.total_unique_replies / calculatedTotals.total_executions * 100);
    calculatedTotals.overall_bounce_rate = (calculatedTotals.total_bounces / calculatedTotals.total_executions * 100);
  }

  const getSequenceName = () => {
    const name = sequenceDetails?.name || 
                sequenceDetails?.sequenceName || 
                sequenceDetails?.title ||
                metrics?.sequence_name || 
                metrics?.sequenceName || 
                metrics?.name || 
                metrics?.title;
    console.log("Sequence name lookup:", { sequenceDetails, metrics, name });
    return name || null;
  };

  const safeMetrics = {
    ...metrics,
    totals: Object.keys(metrics?.totals || {}).length > 0 ? metrics.totals : calculatedTotals,
    steps: steps,
    stepMetrics: steps,
    sequence_name: getSequenceName(),
    total_members: sequenceDetails?.totalMembers || sequenceDetails?.total_members || metrics?.total_members || metrics?.totalMembers || 0,
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        {showTitle ? (
          <Typography variant="h6">Sequence Metrics</Typography>
        ) : (
          <Box />
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setAddMetricOpen(true)}
          disabled={loading || !sequenceId || isDemoMode}
        >
          Add metric
        </Button>
      </Stack>

      <Box mb={4}>
        <Typography variant="subtitle1" gutterBottom component="div">
          Name:{" "}
          {loading ? (
            <Skeleton
              variant="rounded"
              width={150}
              height={32}
              sx={{ display: "inline-block", verticalAlign: "middle" }}
            />
          ) : (
            <Chip label={safeMetrics.sequence_name || `Sequence ${sequenceId?.slice(-8)}`} />
          )}
        </Typography>
        {!loading && safeMetrics.total_members > 0 && (
          <Typography variant="subtitle1" gutterBottom>
            Total Members: <Chip label={safeMetrics.total_members} />
          </Typography>
        )}
      </Box>

      <Typography variant="h6" gutterBottom>
        Overall Performance
      </Typography>
      <SequenceMetricsCards
        totals={safeMetrics.totals}
        showPipelineValue={sequenceDetails?.prospectValue > 0}
        customPipelineValue={
          sequenceDetails?.prospectValue > 0
            ? ((safeMetrics.totals.total_unique_replies || 0) + (safeMetrics.totals.total_bookings || 0)) * (sequenceDetails?.prospectValue || 1000)
            : null
        }
        pipelineValueTooltip={
          sequenceDetails?.prospectValue > 0
            ? `Estimated value based on positive replies and meeting bookings multiplied by your prospect value ($${(sequenceDetails?.prospectValue || 1000).toLocaleString()} per outcome). Configure prospect value in sequence settings.`
            : undefined
        }
        showPendingApprovals={true}
        onPendingApprovalsClick={() => setApprovalsOpen(true)}
        loading={loading}
      />

      <Divider sx={{ my: 3 }} />
      <ActivityTimeline
        sequenceId={sequenceId}
        companyId={companyId}
        getToken={getToken}
      />

      {!loading && safeMetrics.stepMetrics && safeMetrics.stepMetrics.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Step-by-Step Performance
          </Typography>
          {safeMetrics.stepMetrics.map((step: any, index: number) => {
            const stepMetrics = getStepTypeMetrics(step);
            const stepOrder = step.stepOrder || step.step_order || index + 1;
            const stepTitle = step.stepName || step.step_name || step.step_subject || step.stepSubject || `${getStepTypeLabel(step)}`;
            const totalExecutions = step.totalExecutions || step.total_executions || step.executedCount || 0;
            const hasVariants = step.abTestEnabled && step.variantMetrics && step.variantMetrics.length > 0;

            return (
              <Box key={step.stepId || step.step_id || index} mb={3}>
                <Box display="flex" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                  {getStepIcon(step)}
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ ml: 1, mr: 1 }}
                  >
                    Step {stepOrder}: {stepTitle}
                  </Typography>
                  <Chip
                    label={getStepTypeLabel(step)}
                    size="small"
                    variant="outlined"
                  />
                  {step.abTestEnabled && (
                    <Chip
                      icon={<FlaskConical size={14} />}
                      label="A/B Test"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Show variant breakdown when A/B testing is enabled */}
                {hasVariants ? (
                  <Box sx={{ pl: 4 }}>
                    {step.variantMetrics.map((variant: any, vIndex: number) => {
                      const variantLabel = String.fromCharCode(65 + vIndex); // A, B, C, D...
                      return (
                        <Box
                          key={variant.variantId}
                          sx={{
                            mb: 2,
                            pl: 2,
                            borderLeft: 3,
                            borderColor: variant.variantId === step.statisticalAnalysis?.winningVariantId
                              ? "primary.main"
                              : "divider",
                          }}
                        >
                          <Box display="flex" alignItems="center" mb={1} gap={1}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              color={variant.variantId === step.statisticalAnalysis?.winningVariantId ? "primary" : "text.primary"}
                            >
                              {variantLabel}: {variant.name}
                            </Typography>
                            {variant.variantId === step.statisticalAnalysis?.winningVariantId && (
                              <Chip
                                label="Winner"
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={4} md={2}>
                              <MetricCard
                                icon={getStepIcon(step)}
                                title="Sent"
                                value={variant.executions || 0}
                              />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <MetricCard
                                icon={<Eye size={24} />}
                                title="Opens"
                                value={variant.opens || 0}
                                percentage={variant.openRate || 0}
                              />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <MetricCard
                                icon={<MousePointer size={24} />}
                                title="Clicks"
                                value={variant.clicks || 0}
                                percentage={variant.clickRate || 0}
                              />
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <MetricCard
                                icon={<Reply size={24} />}
                                title="Replies"
                                value={variant.replies || 0}
                                percentage={variant.replyRate || 0}
                              />
                            </Grid>
                            {(variant.bookings > 0 || variant.bookingRate > 0) && (
                              <Grid item xs={6} sm={4} md={2}>
                                <MetricCard
                                  icon={<Calendar size={24} />}
                                  title="Bookings"
                                  value={variant.bookings || 0}
                                  percentage={variant.bookingRate || 0}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })}

                    {/* Statistical Analysis Accordion */}
                    {step.statisticalAnalysis && (
                      <Accordion
                        sx={{
                          mt: 2,
                          backgroundColor: "background.paper",
                          "&:before": { display: "none" },
                        }}
                        disableGutters
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 40,
                            "& .MuiAccordionSummary-content": { my: 1 },
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <FlaskConical size={16} />
                            <Typography variant="body2" fontWeight={500}>
                              Statistical Analysis
                            </Typography>
                            {step.statisticalAnalysis.hasSignificantWinner && (
                              <Chip
                                label="Winner Found"
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                            {!step.statisticalAnalysis.sampleSizeSufficient && (
                              <Chip
                                label="Collecting Data"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <ABTestMetricsCard
                            variantMetrics={step.variantMetrics}
                            statisticalAnalysis={step.statisticalAnalysis}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                ) : (
                  /* Standard metrics display when no A/B testing */
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                      <MetricCard
                        icon={getStepIcon(step)}
                        title={getStepTypeLabel(step) === "Profile Visit" ? "Visited" :
                               getStepTypeLabel(step) === "Connection Request" ? "Sent" :
                               getStepTypeLabel(step) === "LinkedIn Message" ? "Sent" :
                               getStepTypeLabel(step) === "Email" ? "Sent" :
                               getStepTypeLabel(step) === "SMS" ? "Sent" :
                               getStepTypeLabel(step) === "Voice Message" ? "Sent" :
                               getStepTypeLabel(step) === "Browser Task" ? "Executed" : "Completed"}
                        value={totalExecutions}
                      />
                    </Grid>
                    {stepMetrics.primaryMetric && (
                      <Grid item xs={6} sm={4} md={2}>
                        <MetricCard
                          icon={stepMetrics.primaryMetric.icon}
                          title={stepMetrics.primaryMetric.title}
                          value={stepMetrics.primaryMetric.value}
                          percentage={stepMetrics.primaryMetric.percentage}
                          tooltip={stepMetrics.primaryMetric.tooltip}
                        />
                      </Grid>
                    )}
                    {stepMetrics.secondaryMetric && (
                      <Grid item xs={6} sm={4} md={2}>
                        <MetricCard
                          icon={stepMetrics.secondaryMetric.icon}
                          title={stepMetrics.secondaryMetric.title}
                          value={stepMetrics.secondaryMetric.value}
                          percentage={stepMetrics.secondaryMetric.percentage}
                          tooltip={stepMetrics.secondaryMetric.tooltip}
                        />
                      </Grid>
                    )}
                    {stepMetrics.tertiaryMetric && (
                      <Grid item xs={6} sm={4} md={2}>
                        <MetricCard
                          icon={stepMetrics.tertiaryMetric.icon}
                          title={stepMetrics.tertiaryMetric.title}
                          value={stepMetrics.tertiaryMetric.value}
                          percentage={stepMetrics.tertiaryMetric.percentage}
                          tooltip={stepMetrics.tertiaryMetric.tooltip}
                        />
                      </Grid>
                    )}
                    {stepMetrics.quaternaryMetric && (
                      <Grid item xs={6} sm={4} md={2}>
                        <MetricCard
                          icon={stepMetrics.quaternaryMetric.icon}
                          title={stepMetrics.quaternaryMetric.title}
                          value={stepMetrics.quaternaryMetric.value}
                          percentage={stepMetrics.quaternaryMetric.percentage}
                          tooltip={stepMetrics.quaternaryMetric.tooltip}
                        />
                      </Grid>
                    )}
                    {sequenceDetails?.prospectValue > 0 && ((step.unique_replies || 0) + (step.unique_bookings || 0)) > 0 && (
                      <Grid item xs={6} sm={4} md={2}>
                        <MetricCard
                          icon={<CircleDollarSign size={24} />}
                          title="Pipeline Value"
                          value={`$${(((step.unique_replies || 0) + (step.unique_bookings || 0)) * (sequenceDetails?.prospectValue || 1000)).toLocaleString()}`}
                          tooltip={`Value from this step: positive replies and bookings × $${(sequenceDetails?.prospectValue || 1000).toLocaleString()}`}
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            );
          })}
        </>
      )}

      <SequenceActionsDialog
        open={approvalsOpen}
        onClose={() => setApprovalsOpen(false)}
        companyId={companyId}
        sequence={{ id: sequenceId }}
        getToken={getToken}
        initialFilter="unapproved"
      />

      <AddManualMetricDialog
        open={addMetricOpen}
        onClose={() => setAddMetricOpen(false)}
        onAdded={loadMetrics}
        sequenceId={sequenceId}
        companyId={companyId}
        steps={safeMetrics.stepMetrics || []}
        getToken={getToken}
      />
    </Box>
  );
};
